/**
 * Demo-mode overlay — session-scoped fabricated state.
 *
 * Records the effects of SIMULATED portal actions (staff demo mode) so
 * follow-up GETs can present them as if they happened. Pure module state:
 * no imports, no persistence — a reload or demo-toggle resets everything,
 * which is the demo "reset" behavior working as intended.
 *
 * Plan: knowledge/portal/plans/2026-07-16-portal-demo-mode.md
 */

export interface DemoUploadedDoc {
  driverId: string
  fileType: string
  fileName: string
}

export interface DemoRequestedDoc {
  driverId: string
  items: string[] // file_type keys requested from the driver
}

/** Mirrors AccidentsView.vue's CrashReport. */
export interface DemoCrashReport {
  filename: string
  mime_type: string | null
  file_size: number | null
  uploaded_at: string
  uploaded_by: string | null
}

/** Mirrors AccidentsView.vue's AccidentRow so the registry merge is seamless. */
export interface DemoAccident {
  id: string
  accident_date: string
  accident_time: string | null
  location_city: string | null
  location_state: string | null
  driver_id: string | null
  driver_name: string | null
  vehicle_vin: string | null
  fatality_count: number
  injury_count: number
  disabling_damage_tow: boolean
  hazmat_released: boolean
  recordable: boolean
  notes: string | null
  crash_report: DemoCrashReport | null
  created_at: string
  updated_at: string
}

interface DemoOverlayState {
  uploadedDocs: DemoUploadedDoc[]
  requestedDocs: DemoRequestedDoc[]
  accidents: DemoAccident[]
  /** id → full_name, harvested from /v1/client/drivers list GETs while demo
   *  is on (adapter.ts), so fabricated accidents can render the picked
   *  driver's name instead of "—". */
  driverNames: Record<string, string>
  /** accident id → the actual File "uploaded" as its crash report, so the
   *  row's View action can serve the real bytes back (api.ts blob adapter). */
  crashReportFiles: Record<string, File>
  counter: number
}

function freshState(): DemoOverlayState {
  return {
    uploadedDocs: [],
    requestedDocs: [],
    accidents: [],
    driverNames: {},
    crashReportFiles: {},
    counter: 0,
  }
}

export const demoOverlay: DemoOverlayState = freshState()

export function resetDemoOverlay(): void {
  Object.assign(demoOverlay, freshState())
}

export function nextDemoId(): string {
  demoOverlay.counter += 1
  return `demo-${demoOverlay.counter}`
}
